/**
 * <edena-app> - Composant racine de l'application
 */

import { initRouter, registerRoute, getCurrentPath, navigateTo } from '@/lib/router.js';
import { setState, getState, loadDefaultEtablissement, subscribe } from '@/lib/store.js';
import { onAuthStateChange } from '@/lib/supabaseClient.js';
import { upsertUtilisateurLogin } from '@/lib/api.js';
import { ensureUiStaffLabelLoaded } from '@/lib/ui-labels.js';
import { syncPublicGoogleTag } from '@/lib/google-public-tag.js';
import './public/nav-bar.js';
import './toast-message.js';
import { captureUtmParams } from '@/lib/utm-tracker.js';

const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || 'dev';

// Import lazy des pages
const loadPage = {
  home: () => import('./public/page-home.js'),
  services: () => import('./public/page-services.js'),
  offres: () => import('./public/page-offres.js'),
  abonnements: () => import('./public/page-abonnements.js'),
  team: () => import('./public/page-team.js'),
  booking: () => import('./public/page-booking.js'),
  account: () => import('./public/page-account.js'),
  faq: () => import('./public/page-faq.js'),
  admin: () => import('./admin/page-admin.js'),
  superAdmin: () => import('./admin/page-super-admin.js'),
  authCallback: () => import('./public/page-auth-callback.js'),
  thankYou: () => import('./public/page-thank-you.js')
};

class EdenaApp extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentPage = null;
    this.isAdminMode = false;
    this._lastLoadedUserId = null;
  }

  connectedCallback() {
    // 0) Capturer les UTMs
    captureUtmParams();

    // Détecter si on est sur /admin dès le chargement
    const currentPath = getCurrentPath();
    this.isAdminMode = currentPath === '/admin';
    syncPublicGoogleTag(currentPath);
    
    // 0) Capturer une éventuelle cible de redirection passée en query avant le hash
    try {
      if (window.location.search) {
        const paramsSearch = new URLSearchParams(window.location.search);
        const redirectTarget = paramsSearch.get('redirect');
        if (redirectTarget) {
          // Normaliser/décoder au maximum avant stockage
          let v = String(redirectTarget);
          try {
            for (let i = 0; i < 3 && /%[0-9A-Fa-f]{2}/.test(v); i++) v = decodeURIComponent(v);
          } catch {}
          if (!v.startsWith('/')) v = '/' + v.replace(/^\/*/, '');

          try { localStorage.setItem('post_login_redirect', v); } catch {}
          try {
            const newState = { ...(window.history.state || {}), redirect: v };
            // Nettoyer la query de l'URL pour éviter interférences ultérieures
            const hash = window.location.hash || '#/';
            const cleanUrl = `${window.location.origin}/${hash}`;
            window.history.replaceState(newState, '', cleanUrl);
          } catch {}
        }
      }
    } catch {}

    this.render();
    this.setupRouter();
    this.setupAuth();
    this.setupStoreSubscriptions();
    this.setupEventListeners();

    // Fallback temporisé: après init auth, si on reste sur /login authentifié, forcer /admin
    try {
      setTimeout(() => {
        try {
          const isAuth = getState('isAuthenticated');
          const pathNow = getCurrentPath();
          if (isAuth && (pathNow === '/login' || pathNow === '/auth/callback')) {
            // Respecter une cible explicite si présente
            let pending = null;
            try { pending = localStorage.getItem('post_login_redirect'); } catch {}
            if (pending) {
              navigateTo(pending);
              return;
            }
            // Rester côté public par défaut
            navigateTo('/');
          }
        } catch {}
      }, 1200);
    } catch {}
  }

  disconnectedCallback() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
    }
  }

  setupStoreSubscriptions() {
    if (this.unsubscribeStore) return;

    this.unsubscribeStore = subscribe('*', (nextState, prevState) => {
      const nextSiteId = nextState?.currentSite?.id || null;
      const prevSiteId = prevState?.currentSite?.id || null;
      const nextEtabId = nextState?.currentEtablissement?.id || null;
      const prevEtabId = prevState?.currentEtablissement?.id || null;

      if (nextSiteId && nextEtabId && (nextSiteId !== prevSiteId || nextEtabId !== prevEtabId)) {
        ensureUiStaffLabelLoaded().catch(() => {});
      }
    });
  }

  async setupAuth() {
    const { supabase } = await import('@/lib/supabaseClient.js');

    const loadUserProfile = async (user) => {
      if (this._lastLoadedUserId === user.id) {
        return;
      }
      
      const { data: baseUser, error: userError } = await supabase
        .from('utilisateurs')
        .select('id, role')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (userError) {
        console.error('Erreur lors du chargement utilisateur.', userError);
        return;
      }

      if (!baseUser) {
        // Fallback: chercher par email (compatibilité)
        const { data: byEmail, error: emailError } = await supabase
          .from('utilisateurs')
          .select('id, role')
          .eq('email', user.email)
          .maybeSingle();

        if (emailError) {
          console.error('Erreur lors de la recherche par email.', emailError);
          setState({ userRole: null, userProfile: { prenom: user.email, nom: '' } });
          return;
        }

        if (!byEmail) {
          // État neutre pour éviter les appels suivants dépendants de baseUser.id
          setState({ userRole: null, userProfile: { prenom: user.email, nom: '' } });
          return;
        }

        // Utiliser l'enregistrement trouvé par email comme baseUser
        const baseUserResolved = byEmail;
        setState({ userRole: baseUserResolved.role });
        
        try {
          await supabase
            .from('utilisateurs')
            .update({ auth_user_id: user.id })
            .eq('id', baseUserResolved.id);
        } catch {}

        // 2. Charger le profil détaillé en fonction du rôle
        let profile = null;
        const isAdminRole2 = ['super_admin', 'admin', 'manager'].includes(baseUserResolved.role);
        if (isAdminRole2) {
          profile = { prenom: user.email, nom: '' };
        } else if (baseUserResolved.role === 'client') {
          const { data } = await supabase
            .from('clients')
            .select('id')
            .eq('utilisateur_id', baseUserResolved.id)
            .maybeSingle();
          profile = data ? { prenom: user.email, nom: '' } : null;
        } else {
          const { data } = await supabase
            .from('employes')
            .select('nom_complet')
            .eq('utilisateur_id', baseUserResolved.id)
            .maybeSingle();
          profile = data ? { prenom: data.nom_complet || user.email, nom: '' } : null;
        }

        if (profile) {
          setState({ userProfile: profile });
        } else {
          setState({ userProfile: { prenom: user.email, nom: '' } });
          // Ne pas afficher de warning inutile pour les rôles admin
        }
        return;
      }

      setState({ userRole: baseUser.role });
      
      // 2. Charger le profil détaillé en fonction du rôle
      let profile = null;
      const isAdminRole = ['super_admin', 'admin', 'manager'].includes(baseUser.role);
      if (isAdminRole) {
        // Rôles admin: pas de profil lié requis
        profile = { prenom: user.email, nom: '' };
      } else if (baseUser.role === 'client') {
        // Clients n'ont plus prenom/nom en colonnes; on vérifie simplement l'existence
        const { data } = await supabase
          .from('clients')
          .select('id')
          .eq('utilisateur_id', baseUser.id)
          .maybeSingle();
        profile = data ? { prenom: user.email, nom: '' } : null;
      } else {
        // Employés: utiliser uniquement nom_complet
        const { data } = await supabase
          .from('employes')
          .select('nom_complet')
          .eq('utilisateur_id', baseUser.id)
          .maybeSingle();
        profile = data ? { prenom: data.nom_complet || user.email, nom: '' } : null;
      }

      if (profile) {
        setState({ userProfile: profile });
      } else {
        // Fallback pour les utilisateurs sans profil (ex: super_admin)
        setState({ userProfile: { prenom: user.email, nom: '' } });
        // Ne pas afficher de warning inutile pour les rôles admin
      }

      // Tenter de charger les établissements accessibles (pas de fallback par défaut souhaité)
      try {
        const { getState: getAppState, loadUserEtablissements } = await import('@/lib/store.js');
        const st = getAppState();
        if (!st.currentEtablissement || !st.currentSite) {
          await loadUserEtablissements();
        }
      } catch {
      }
      this._lastLoadedUserId = user.id;
    };

    this.unsubscribeAuth = onAuthStateChange(async (event, session) => {
      const user = session?.user || null;
      setState({ user, isAuthenticated: !!user });
      if (user) {
        // Upsert & mise à jour de derniere_connexion_at AVANT chargement du profil
        await upsertUtilisateurLogin().catch(() => {});
        await loadUserProfile(user);
        // Rediriger vers la cible mémorisée, quelle que soit la route actuelle
        try {
          // 1) state (défini par navigateTo('/login', { redirect }))
          let redirectTarget = window.history.state?.redirect || null;
          // 2) query AVANT hash (ex: /?redirect=/admin#/login)
          if (!redirectTarget && window.location.search) {
            const paramsSearch = new URLSearchParams(window.location.search);
            redirectTarget = paramsSearch.get('redirect') || redirectTarget;
          }
          // 3) hash query (ex: #/login?redirect=/admin ou #/?redirect=/admin)
          if (!redirectTarget && window.location.hash.includes('?')) {
            const qs = window.location.hash.split('?')[1]?.split('#')[0] || '';
            const params = new URLSearchParams(qs);
            redirectTarget = params.get('redirect') || redirectTarget;
          }
          // 4) localStorage fallback
          if (!redirectTarget) {
            redirectTarget = localStorage.getItem('post_login_redirect') || null;
          }

          // Normaliser (gérer %2F et %252F etc.)
          const normalize = (val) => {
            if (!val) return val;
            let v = String(val);
            try {
              // Décoder jusqu'à 3 fois au max pour éviter boucles
              for (let i = 0; i < 3 && /%[0-9A-Fa-f]{2}/.test(v); i++) {
                v = decodeURIComponent(v);
              }
            } catch {}
            // S'assurer qu'on a un chemin absolu commençant par '/'
            if (!v.startsWith('/')) v = '/' + v.replace(/^\/*/, '');
            return v;
          };

          redirectTarget = normalize(redirectTarget);

          if (redirectTarget) {
            localStorage.removeItem('post_login_redirect');
            navigateTo(redirectTarget);
          } else {
            // Sans cible explicite, rester sur la page actuelle sauf si on est sur l'écran d'auth
            try {
              const pathNow = getCurrentPath();
              const isAuthRoute = pathNow === '/login' || pathNow === '/auth/callback';
              if (isAuthRoute) {
                navigateTo('/');
              }
            } catch {}
          }
        } catch {}
      } else {
        this._lastLoadedUserId = null;
      }
    });

    const { currentUser } = await import('@/lib/supabaseClient.js');
    const user = await currentUser();
    if (user) {
      // Assurer l'upsert avant de lire les tables protégées par RLS
      await upsertUtilisateurLogin().catch(() => {});
      await loadUserProfile(user);
      // Redirection post-auth si session déjà présente au chargement
      try {
        let redirectTarget = window.history.state?.redirect || null;
        if (!redirectTarget && window.location.search) {
          const paramsSearch = new URLSearchParams(window.location.search);
          redirectTarget = paramsSearch.get('redirect') || redirectTarget;
        }
        if (!redirectTarget && window.location.hash.includes('?')) {
          const qs = window.location.hash.split('?')[1]?.split('#')[0] || '';
          const params = new URLSearchParams(qs);
          redirectTarget = params.get('redirect') || redirectTarget;
        }
        if (!redirectTarget) {
          redirectTarget = localStorage.getItem('post_login_redirect') || null;
        }
        const normalize = (val) => {
          if (!val) return val;
          let v = String(val);
          try {
            for (let i = 0; i < 3 && /%[0-9A-Fa-f]{2}/.test(v); i++) {
              v = decodeURIComponent(v);
            }
          } catch {}
          if (!v.startsWith('/')) v = '/' + v.replace(/^\/*/, '');
          return v;
        };
        redirectTarget = normalize(redirectTarget);
        if (redirectTarget) {
          localStorage.removeItem('post_login_redirect');
          navigateTo(redirectTarget);
        } else {
          try {
            const pathNow = getCurrentPath();
            const isAuthRoute = pathNow === '/login' || pathNow === '/auth/callback';
            if (isAuthRoute) {
              navigateTo('/');
            }
          } catch {}
        }
      } catch {}
    }
  }

  async ensureEtablissementLoaded() {
    try {
      const { isAuthenticated, currentEtablissement, currentSite } = getState();

      // Déjà chargé
      if (currentEtablissement && currentSite) {
        await ensureUiStaffLabelLoaded().catch(() => {});
        return;
      }

      if (isAuthenticated) {
        // Utilisateur connecté : tenter de charger ses établissements
        try {
          const { loadUserEtablissements, getState: getStoreState } = await import('@/lib/store.js');
          await loadUserEtablissements();
          const { currentEtablissement: ce2, currentSite: cs2 } = getStoreState();
          if (ce2 && cs2) {
            return;
          }
        } catch (e) {
          console.error('Erreur lors du chargement des établissements utilisateur:', e);
        }
      }

      // Fallback : utilisateur non authentifié OU utilisateur authentifié sans établissement associé (client)
      // → charger l'établissement par défaut pour permettre la navigation publique
      await loadDefaultEtablissement();
      await ensureUiStaffLabelLoaded().catch(() => {});
    } catch (error) {
      console.error('Erreur chargement établissement par défaut pour la partie publique', error);
    }
  }

  setupRouter() {
    // Enregistrer les routes avec re-render du layout
    registerRoute('/', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('home', 'page-home');
    });
    registerRoute('/services', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('services', 'page-services');
    });
    registerRoute('/offres', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('offres', 'page-offres');
    });
    registerRoute('/abonnements', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('abonnements', 'page-abonnements');
    });
    registerRoute('/team', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('team', 'page-team');
    });
    registerRoute('/faq', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('faq', 'page-faq');
    });
    registerRoute('/booking', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('booking', 'page-booking');
    });
    registerRoute('/merci', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('thankYou', 'page-thank-you');
    });
    registerRoute('/account', async () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      await this.ensureEtablissementLoaded();
      this.loadPageComponent('account', 'page-account');
    });
    registerRoute('/admin', () => {
      // Rediriger vers /login si non authentifié
      const isAuth = getState('isAuthenticated');
      if (!isAuth) {
        try { localStorage.setItem('post_login_redirect', '/admin'); } catch {}
        // Si une auth vient juste d'être initiée, patienter ici et afficher le loader
        let recent = false;
        try {
          const ts = parseInt(localStorage.getItem('auth_recent') || '0', 10);
          if (ts && Date.now() - ts < 8000) recent = true;
        } catch {}

        if (recent) {
          this.isAdminMode = true;
          this.render();
          this.setupEventListeners();
          // Polling court pour attendre l'auth
          let tries = 0;
          const iv = setInterval(() => {
            tries += 1;
            const ok = getState('isAuthenticated');
            if (ok) {
              clearInterval(iv);
              this.loadPageComponent('admin', 'page-admin');
            }
            if (tries >= 40) { // ~8s
              clearInterval(iv);
              navigateTo('/login', { redirect: '/admin' });
            }
          }, 200);
          return;
        }

        navigateTo('/login', { redirect: '/admin' });
        return;
      }
      this.isAdminMode = true;
      this.render();
      this.setupEventListeners();
      this.loadPageComponent('admin', 'page-admin');
    });

    registerRoute('/admin/sms-monitoring', () => {
      const isAuth = getState('isAuthenticated');
      if (!isAuth) {
        try { localStorage.setItem('post_login_redirect', '/admin/sms-monitoring'); } catch {}
        let recent = false;
        try {
          const ts = parseInt(localStorage.getItem('auth_recent') || '0', 10);
          if (ts && Date.now() - ts < 8000) recent = true;
        } catch {}

        if (recent) {
          this.isAdminMode = true;
          this.render();
          this.setupEventListeners();
          let tries = 0;
          const iv = setInterval(() => {
            tries += 1;
            const ok = getState('isAuthenticated');
            if (ok) {
              clearInterval(iv);
              this.loadPageComponent('admin', 'page-admin');
            }
            if (tries >= 40) {
              clearInterval(iv);
              navigateTo('/login', { redirect: '/admin/sms-monitoring' });
            }
          }, 200);
          return;
        }

        navigateTo('/login', { redirect: '/admin/sms-monitoring' });
        return;
      }
      this.isAdminMode = true;
      this.render();
      this.setupEventListeners();
      this.loadPageComponent('admin', 'page-admin');
    });

    registerRoute('/admin/super', () => {
      const isAuth = getState('isAuthenticated');
      if (!isAuth) {
        try { localStorage.setItem('post_login_redirect', '/admin/super'); } catch {}
        let recent = false;
        try {
          const ts = parseInt(localStorage.getItem('auth_recent') || '0', 10);
          if (ts && Date.now() - ts < 8000) recent = true;
        } catch {}

        if (recent) {
          this.isAdminMode = true;
          this.render();
          this.setupEventListeners();
          let tries = 0;
          const iv = setInterval(() => {
            tries += 1;
            const ok = getState('isAuthenticated');
            if (ok) {
              clearInterval(iv);
              this.loadPageComponent('superAdmin', 'page-super-admin');
            }
            if (tries >= 40) {
              clearInterval(iv);
              navigateTo('/login', { redirect: '/admin/super' });
            }
          }, 200);
          return;
        }

        navigateTo('/login', { redirect: '/admin/super' });
        return;
      }

      this.isAdminMode = true;
      this.render();
      this.setupEventListeners();

      const actualUserRole = getState('actualUserRole');
      if (actualUserRole && actualUserRole !== 'super_admin') {
        window.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: 'Accès réservé aux super administrateurs.',
              type: 'warning',
            },
          })
        );
        navigateTo('/admin');
        return;
      }

      this.loadPageComponent('superAdmin', 'page-super-admin');
    });

    // Alias de connexion courte
    registerRoute('/login', () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      // Réutilise la page compte pour l'authentification
      this.loadPageComponent('account', 'page-account');
    });

    // Route de callback d'auth (liens magiques)
    registerRoute('/auth/callback', () => {
      this.isAdminMode = false;
      this.render();
      this.setupEventListeners();
      this.loadPageComponent('authCallback', 'page-auth-callback');
    });

    // Initialiser le router
    initRouter();
  }

  async loadPageComponent(pageName, tagName) {
    const viewContainer = this.shadowRoot.querySelector('#view');
    if (!viewContainer) return;

    // Afficher un loader
    viewContainer.innerHTML = '<div class="loading">Chargement...</div>';

    try {
      // Charger le module de la page
      await loadPage[pageName]();

      // Créer et monter le composant
      viewContainer.innerHTML = '';
      const pageElement = document.createElement(tagName);
      viewContainer.appendChild(pageElement);

      this.currentPage = pageName;
    } catch (error) {
      console.error(`Erreur lors du chargement de la page ${pageName}:`, error);
      viewContainer.innerHTML = '<div class="error">Erreur lors du chargement de la page</div>';
    }
  }

  setupEventListeners() {
    // Éviter les doublons d'event listeners
    if (this._eventListenersSetup) {
      return;
    }
    this._eventListenersSetup = true;
    
    // Écouter les événements de toast
    window.addEventListener('toast:show', (e) => {
      const toast = this.shadowRoot.querySelector('toast-message');
      if (toast) {
        toast.show(e.detail.message, e.detail.type);
      }
    });

    // Écouter les changements de hash pour mettre à jour le layout
    window.addEventListener('hashchange', () => {
      const currentPath = getCurrentPath();
      const wasAdminMode = this.isAdminMode;
      const isAdminRoute = currentPath === '/admin' || currentPath.startsWith('/admin/');
      this.isAdminMode = isAdminRoute;
      syncPublicGoogleTag(currentPath);
      
      // Re-render uniquement si le mode change
      if (wasAdminMode !== this.isAdminMode) {
        this.render();
        this.setupEventListeners();
      }
    });
  }

  render() {
    // Layout simplifié pour le back-office (sans header/footer)
    if (this.isAdminMode) {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            min-height: 100vh;
            background-color: var(--admin-bg, #f6f1e9);
            color: var(--admin-text, #1f2933);
          }

          .admin-view {
            min-height: 100vh;
            background: var(--admin-bg, #f6f1e9);
          }

          .loading,
          .error {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            font-size: var(--font-size-lg);
            color: var(--admin-text-secondary, #4b5563);
          }

          .error {
            color: #ef4444;
          }
        </style>

        <div class="admin-view">
          <main id="view" role="main">
            <div class="loading">Chargement...</div>
          </main>
        </div>

        <toast-message></toast-message>
      `;
      return;
    }

    // Layout normal pour les pages publiques (avec header/footer)
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          min-height: 100vh;
          background-color: var(--color-bg);
        }

        .app-container {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
        }

        header {
          position: sticky;
          top: 0;
          z-index: var(--z-sticky);
          background-color: var(--color-surface);
          box-shadow: var(--shadow-sm);
        }

        main {
          flex: 1;
          padding: var(--space-lg);
        }

        .loading,
        .error {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          font-size: var(--font-size-lg);
          color: var(--color-text-muted);
        }

        .error {
          color: var(--color-error);
        }

        footer {
          padding: var(--space-xl);
          text-align: center;
          background-color: var(--color-surface);
          color: var(--color-text-muted);
          font-size: var(--font-size-sm);
          border-top: 1px solid var(--color-border);
        }

        .version-info {
          font-size: 0.8em;
          opacity: 0.5;
          margin-left: 8px;
        }
      </style>

      <div class="app-container">
        <header>
          <nav-bar></nav-bar>
        </header>

        <main id="view" role="main">
          <div class="loading">Chargement...</div>
        </main>

        <footer>
          <p>&copy; ${new Date().getFullYear()} Edena - Centre esthétique <span class="version-info">v${BUILD_VERSION}</span></p>
        </footer>
      </div>

      <toast-message></toast-message>
    `;
  }
}

customElements.define('edena-app', EdenaApp);
